/*
 * DiagramCard — an offered drawing, and the renderers behind it.
 *
 * The model never emits markup. It supplies a structured spec (see
 * cloud-anchor/pkg/nabxagent/diagram.go) and everything visual is decided here,
 * so drawings are consistent, themed, and safe to render — there is no path by
 * which model output becomes DOM.
 *
 * A drawing renders as soon as it exists. An earlier revision opened collapsed so
 * an answer was never displaced by a picture nobody asked for — but a collapsed
 * card reads as "it didn't draw", which is worse. The toggle stays, so a card
 * can be folded away once it has been read.
 */

import React, { useState } from "react";
import {
  FiChevronRight, FiChevronDown, FiShare2, FiBarChart2, FiGitCommit,
} from "react-icons/fi";

const RENDERERS = {
  topology: Topology,
  pipeline: Pipeline,
  comparison: Comparison,
};

const ICONS = {
  topology: FiShare2,
  pipeline: FiGitCommit,
  comparison: FiBarChart2,
};

// Diagram renders a spec with no card chrome, so a tiered answer can nest one
// in a tab. kind falls back to the spec's own field, which is how compose_answer
// carries it.
export function Diagram({ spec, kind }) {
  const Render = RENDERERS[kind || spec?.kind] || Comparison;
  // Styles ship with the drawing, not with the card. A nested Diagram — inside a
  // tiered answer's tab — has no card around it, and without this the pipeline
  // renders as unstyled divs and every SVG label falls back to black on black.
  return (
    <>
      <Styles />
      <Render spec={spec} />
    </>
  );
}

export default function DiagramCard({ diagram }) {
  const [open, setOpen] = useState(true);
  const Icon = ICONS[diagram.kind] || FiBarChart2;

  return (
    <div className={`dg-card ${open ? "dg-open" : ""}`}>
      <Styles />
      <button className="dg-head" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="dg-chev">{open ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}</span>
        <Icon size={14} className="dg-icon" />
        <span className="dg-title">{diagram.title}</span>
        <span className="dg-action">{open ? "Hide" : "Show"}</span>
      </button>

      {open && (
        <div className="dg-body">
          <Diagram spec={diagram.spec} kind={diagram.kind} />
          {diagram.caption && <p className="dg-caption">{diagram.caption}</p>}
        </div>
      )}
    </div>
  );
}

/* ── Topology ──
   Five columns left to right: LAN, this edge, its uplinks, the anchor, the
   internet. Uplinks stack and the connectors fan out of the edge and converge
   into the anchor, which is the shape of the thing being described — several
   links carrying one tunnel. */

// clamp keeps a detail string inside its box. The schema asks for short ones,
// but a model that writes "active · 10.50.28.38/22 · first in every bond" would
// otherwise overrun the anchor column and collide with its label.
function clamp(text, max) {
  const t = String(text || "");
  return t.length > max ? t.slice(0, max - 1).trimEnd() + "…" : t;
}

const STATE_COLOR = {
  active: "#38BDF8",
  standby: "#64748B",
  degraded: "#FBBF24",
  down: "#F87171",
};

function Topology({ spec }) {
  const links = Array.isArray(spec?.links) ? spec.links.slice(0, 8) : [];
  const anchored = spec?.mode !== "direct" && spec?.anchor;
  if (!links.length) return <Empty />;

  const BOX_W = 122;
  const BOX_H = 50;
  const LINK_H = 44;
  const GAP = 26;
  const rowH = LINK_H + 18;
  const H = Math.max(links.length * rowH + 40, 170);
  const midY = H / 2;

  const colLan = 8;
  const colEdge = colLan + BOX_W + GAP + 20;
  const colLink = colEdge + BOX_W + GAP + 34;
  const LINK_W = 168;
  const colAnchor = colLink + LINK_W + GAP + 34;
  const colNet = colAnchor + BOX_W + GAP + 20;
  const W = (anchored ? colNet + BOX_W : colAnchor + BOX_W) + 8;

  const linkY = (i) => (H - links.length * rowH) / 2 + i * rowH + rowH / 2;

  // Cubic between two points, flat at both ends so lines leave and arrive level.
  const curve = (x1, y1, x2, y2) => {
    const dx = Math.max((x2 - x1) * 0.5, 18);
    return `M${x1},${y1} C${x1 + dx},${y1} ${x2 - dx},${y2} ${x2},${y2}`;
  };

  return (
    <div className="dg-svgwrap">
      <svg viewBox={`0 0 ${W} ${H}`} className="dg-svg" role="img"
           aria-label={`Data path with ${links.length} uplinks`}>
        <defs>
          <linearGradient id="dgEdge" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="rgba(56,189,248,.20)" />
            <stop offset="100%" stopColor="rgba(192,132,252,.20)" />
          </linearGradient>
        </defs>

        {/* Connectors first, so boxes sit on top of the line ends. */}
        {links.map((l, i) => {
          const c = STATE_COLOR[l.state] || STATE_COLOR.standby;
          const dim = l.state === "down";
          const y = linkY(i);
          return (
            <g key={`c${i}`} opacity={dim ? 0.32 : 1}>
              <path d={curve(colEdge + BOX_W, midY, colLink, y)} className="dg-wire" stroke={c} />
              {anchored && (
                <path d={curve(colLink + LINK_W, y, colAnchor, midY)} className="dg-wire" stroke={c} />
              )}
              {l.state === "active" && (
                <circle r="3" fill={c} className="dg-spark">
                  <animateMotion dur="2.6s" repeatCount="indefinite"
                    path={curve(colEdge + BOX_W, midY, colLink, y)} />
                </circle>
              )}
            </g>
          );
        })}
        {anchored && (
          <path d={curve(colAnchor + BOX_W, midY, colNet, midY)} className="dg-wire" stroke="#818CF8" />
        )}

        <Node x={colLan} y={midY - BOX_H / 2} w={BOX_W} h={BOX_H}
              label={spec?.lan?.label || "LAN"} detail={spec?.lan?.detail} />
        <Node x={colEdge} y={midY - BOX_H / 2} w={BOX_W} h={BOX_H}
              label="This edge" detail={spec?.mode} accent fill="url(#dgEdge)" />

        {links.map((l, i) => {
          const c = STATE_COLOR[l.state] || STATE_COLOR.standby;
          const y = linkY(i) - LINK_H / 2;
          return (
            <g key={`l${i}`} opacity={l.state === "down" ? 0.45 : 1}>
              <rect x={colLink} y={y} width={LINK_W} height={LINK_H} rx="7"
                    fill="rgba(255,255,255,.035)" stroke={c} strokeOpacity=".55" />
              <circle cx={colLink + 14} cy={y + LINK_H / 2} r="3.5" fill={c} />
              <text x={colLink + 26} y={y + 19} className="dg-t-name">{clamp(l.name, 20)}</text>
              <text x={colLink + 26} y={y + 33} className="dg-t-sub">
                {clamp([l.state, l.detail].filter(Boolean).join(" · "), 26)}
              </text>
            </g>
          );
        })}

        {anchored && (
          <>
            <Node x={colAnchor} y={midY - BOX_H / 2} w={BOX_W} h={BOX_H}
                  label="Anchor" detail={spec.anchor.label} />
            <Node x={colNet} y={midY - BOX_H / 2} w={BOX_W} h={BOX_H} label="Internet" />
            {spec.anchor.detail && (
              <text x={colAnchor + BOX_W / 2} y={midY + BOX_H / 2 + 16}
                    className="dg-t-note" textAnchor="middle">
                {clamp(spec.anchor.detail, 22)}
              </text>
            )}
          </>
        )}
        {!anchored && (
          <Node x={colAnchor} y={midY - BOX_H / 2} w={BOX_W} h={BOX_H} label="Internet" />
        )}
      </svg>
    </div>
  );
}

function Node({ x, y, w, h, label, detail, accent, fill }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="8"
            fill={fill || "rgba(255,255,255,.045)"}
            stroke={accent ? "rgba(129,140,248,.75)" : "rgba(255,255,255,.16)"} />
      <text x={x + w / 2} y={detail ? y + 21 : y + h / 2 + 4}
            className="dg-t-name" textAnchor="middle">{clamp(label, 16)}</text>
      {detail && (
        <text x={x + w / 2} y={y + 35} className="dg-t-sub" textAnchor="middle">
          {clamp(detail, 17)}
        </text>
      )}
    </g>
  );
}

/* ── Pipeline ──
   Ordered stages with a state each. The visual job is the contrast: which parts
   of the mechanism are actually armed and which are merely present. Off stages
   are drawn dashed and dimmed so that reads at a glance. */

const STAGE_TONE = {
  on: { color: "#38BDF8", label: "on" },
  partial: { color: "#818CF8", label: "partial" },
  off: { color: "#64748B", label: "off" },
  unavailable: { color: "#475569", label: "n/a" },
};

function Pipeline({ spec }) {
  const stages = Array.isArray(spec?.stages) ? spec.stages.slice(0, 10) : [];
  if (!stages.length) return <Empty />;

  return (
    <div className="dg-pipe">
      {stages.map((s, i) => {
        const tone = STAGE_TONE[s.state] || STAGE_TONE.off;
        const muted = s.state === "off" || s.state === "unavailable";
        return (
          <React.Fragment key={i}>
            <div className={`dg-stage ${muted ? "dg-muted" : ""}`}
                 style={{ borderColor: tone.color, animationDelay: `${i * 70}ms` }}>
              <div className="dg-stage-top">
                <span className="dg-dot" style={{ background: tone.color }} />
                <span className="dg-stage-label">{s.label}</span>
              </div>
              {s.detail && <div className="dg-stage-detail">{s.detail}</div>}
              <div className="dg-stage-state" style={{ color: tone.color }}>{tone.label}</div>
            </div>
            {i < stages.length - 1 && (
              <div className="dg-arrow" aria-hidden="true">
                <span style={{ background: muted ? "#334155" : "#4B5563" }} />
                <i style={{ borderLeftColor: muted ? "#334155" : "#4B5563" }} />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ── Comparison ──
   DOM rather than SVG: the text stays crisp at any zoom and the layout reflows
   on a narrow window, which matters more here than export fidelity. */

function Comparison({ spec }) {
  const items = Array.isArray(spec?.items) ? spec.items.slice(0, 14) : [];
  if (!items.length) return <Empty />;

  const values = items.map((i) => Number(i.value) || 0);
  const max = Math.max(...values, 0) || 1;
  const min = Math.min(...values);
  const lowerBetter = Boolean(spec?.lower_is_better);
  const unit = spec?.unit || "";

  // Colour by rank, not by threshold — the model supplies no thresholds, and a
  // relative scale is honest about being a comparison rather than a verdict.
  const tone = (v) => {
    if (max === min) return "#818CF8";
    const t = (v - min) / (max - min);
    const good = lowerBetter ? 1 - t : t;
    if (good > 0.66) return "#38BDF8";
    if (good > 0.33) return "#818CF8";
    return "#C084FC";
  };

  return (
    <div className="dg-bars">
      {items.map((it, i) => {
        const v = Number(it.value) || 0;
        const pct = Math.max((v / max) * 100, 1.5);
        return (
          <div className="dg-row" key={i} style={{ animationDelay: `${i * 60}ms` }}>
            <div className="dg-lab" title={it.label}>{it.label}</div>
            <div className="dg-track">
              <div className="dg-fill" style={{ width: `${pct}%`, background: tone(v), animationDelay: `${i * 60}ms` }} />
            </div>
            <div className="dg-val">
              <b>{formatValue(v)}</b>{unit && <span className="dg-unit">{unit}</span>}
              {it.detail && <span className="dg-detail">{it.detail}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Trim float noise without lying about magnitude.
function formatValue(v) {
  if (!isFinite(v)) return "—";
  if (Math.abs(v) >= 1000) return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (Number.isInteger(v)) return String(v);
  return v.toFixed(Math.abs(v) < 10 ? 2 : 1).replace(/\.?0+$/, "");
}

function Empty() {
  return <p className="dg-empty">This diagram arrived without anything to draw.</p>;
}

function Styles() {
  return (
    <style>{`
.dg-card{
  border:1px solid rgba(129,140,248,.30);border-radius:12px;overflow:hidden;
  background:linear-gradient(180deg,rgba(129,140,248,.07),rgba(255,255,255,.015));
  transition:border-color .2s;
}
.dg-card.dg-open{border-color:rgba(129,140,248,.48)}
.dg-head{
  display:flex;align-items:center;gap:9px;width:100%;padding:11px 14px;
  background:none;border:0;cursor:pointer;color:#DDE1EC;font:inherit;font-size:13.5px;text-align:left;
}
.dg-head:hover{background:rgba(255,255,255,.03)}
.dg-chev{display:flex;color:#8A90A6}
.dg-icon{color:#A5B4FC;flex:none}
.dg-title{font-weight:600;flex:1}
.dg-action{
  font-size:11px;letter-spacing:.09em;text-transform:uppercase;color:#A5B4FC;
  border:1px solid rgba(129,140,248,.35);border-radius:999px;padding:3px 10px;
}
.dg-body{padding:6px 14px 14px;animation:dgFade .28s ease both}
@keyframes dgFade{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:none}}
@keyframes dgRise{from{opacity:0;transform:translateY(7px)}to{opacity:1;transform:none}}
@keyframes dgGrow{from{transform:scaleX(0)}to{transform:scaleX(1)}}
@keyframes dgDraw{to{stroke-dashoffset:0}}
.dg-caption{margin:10px 0 0;font-size:12.5px;color:#8A90A6;line-height:1.5}
.dg-empty{margin:0;font-size:13px;color:#8A90A6}

/* Topology */
.dg-svgwrap{overflow-x:auto;padding:4px 0}
.dg-svg{width:100%;min-width:620px;height:auto;display:block}
.dg-wire{
  fill:none;stroke-width:1.6;stroke-linecap:round;opacity:.75;
  stroke-dasharray:400;stroke-dashoffset:400;
  animation:dgDraw .8s cubic-bezier(.4,0,.2,1) .1s forwards;
}
.dg-spark{opacity:.9}
.dg-t-name{
  fill:#E8EAF2;
  font-family:'Inter',ui-sans-serif,system-ui,sans-serif;font-size:11.5px;
  font-weight:600;fill:#E8EAF2;
}
.dg-t-sub{
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:9.5px;fill:#8A90A6;
}
.dg-t-note{
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:9.5px;fill:#6F758B;
}

/* Pipeline */
.dg-pipe{
  display:flex;align-items:stretch;flex-wrap:wrap;gap:0;padding:8px 0 2px;
}
.dg-stage{
  flex:1 1 118px;min-width:118px;
  animation:dgRise .34s cubic-bezier(.4,0,.2,1) both;
  border:1px solid;border-radius:9px;padding:10px 11px;
  background:rgba(255,255,255,.035);
  display:flex;flex-direction:column;gap:3px;
}
.dg-stage.dg-muted{
  border-style:dashed;background:rgba(255,255,255,.015);opacity:.62;
}
.dg-stage-top{display:flex;align-items:center;gap:7px}
.dg-dot{width:6px;height:6px;border-radius:50%;flex:none}
.dg-stage-label{font-size:12.5px;font-weight:600;color:#E8EAF2}
.dg-stage-detail{
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
  font-size:10.5px;color:#8A90A6;line-height:1.4;word-break:break-word;
}
.dg-stage-state{
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
  font-size:9.5px;letter-spacing:.1em;text-transform:uppercase;margin-top:auto;padding-top:4px;
}
.dg-arrow{display:flex;align-items:center;flex:none;padding:0 2px}
.dg-arrow span{display:block;width:12px;height:1.5px}
.dg-arrow i{
  display:block;width:0;height:0;
  border-top:4px solid transparent;border-bottom:4px solid transparent;
  border-left:6px solid;
}
@media (max-width:620px){
  .dg-pipe{flex-direction:column}
  .dg-arrow{transform:rotate(90deg);align-self:center;padding:4px 0}
}

/* Comparison */
.dg-bars{display:flex;flex-direction:column;gap:9px;padding-top:4px}
.dg-row{
  display:grid;grid-template-columns:minmax(78px,132px) 1fr auto;gap:12px;align-items:center;
  animation:dgRise .3s cubic-bezier(.4,0,.2,1) both;
}
.dg-lab{
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:11.5px;color:#C7CCDC;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
}
.dg-track{height:9px;border-radius:999px;background:rgba(255,255,255,.06);overflow:hidden}
.dg-fill{
  height:100%;border-radius:999px;transform-origin:left center;
  animation:dgGrow .55s cubic-bezier(.4,0,.2,1) both;
}
.dg-val{font-size:12px;color:#C7CCDC;white-space:nowrap;display:flex;align-items:baseline;gap:4px}
.dg-val b{font-variant-numeric:tabular-nums;color:#fff;font-weight:600}
.dg-unit{color:#8A90A6;font-size:11px}
.dg-detail{color:#6F758B;font-size:11px;margin-left:4px}

@media (prefers-reduced-motion:reduce){
  .dg-spark{display:none}
  .dg-body,.dg-stage,.dg-row,.dg-fill,.dg-wire{animation:none}
  .dg-wire{stroke-dashoffset:0}
}
`}</style>
  );
}
